import {
  Table,
  type TableColumnDefinition,
  type TableProps,
} from "$components/table";
import { makeStyles } from "@material-ui/core";
import { useState } from "react";
import { useLocalStorage } from "usehooks-ts";

import ValidationTableCell, {
  type ItemKeyValidationDetails,
} from "./validation-table-cell";

const useStyles = makeStyles((t) => ({
  errorRow: {
    // Set the opacity a bit to lighten, works because the values are hex codes
    background: `${t.palette.error.main}38`,
  },
  addedRow: {
    background: `${t.palette.success.main}38`,
  },
  updatedRow: {
    background: `${t.palette.warning.main}38`,
  },
  errorCell: {
    background: `${t.palette.error.main}83`,
  },
  addedCell: {
    background: `${t.palette.success.main}83`,
  },
  updatedCell: {
    background: `${t.palette.warning.main}83`,
  },
}));

const getRowClass =
  (classes: ReturnType<typeof useStyles>): TableProps["classes"]["rowClass"] =>
  (row) => {
    const { hasError, isUpdate, isAddition } = row["Row Status"] || {};
    if (hasError) {
      return classes.errorRow;
    }
    if (isUpdate) {
      return classes.updatedRow;
    }
    if (isAddition) {
      return classes.addedRow;
    }
    return "";
  };

const getRowCellClass =
  (
    classes: ReturnType<typeof useStyles>,
  ): TableProps["classes"]["rowCellClass"] =>
  (row, _index, value = {}) => {
    const { status, errorMessage } = value;
    if (errorMessage) {
      return classes.errorCell;
    }
    if (status === "DELETION") {
      return classes.updatedCell;
    }
    if (status === "UPDATE") {
      return classes.updatedCell;
    }
    // If the whole row is new, don't light up all cells saying they are new
    if (!row["Row Status"].isAddition && status === "ADDITION") {
      return classes.addedCell;
    }
    return "";
  };

export type Data = ReadonlyArray<{
  readonly id: string;
  readonly isUpdate?: boolean;
  readonly isAddition?: boolean;
  readonly hasError?: boolean;
  readonly errorMessage?: string;
  readonly validationBreakdown?: Record<string, ItemKeyValidationDetails>;
}>;

const PAGE_SIZE_OPTIONS = [20, 50, 100];

interface Props {
  data: Data;
  loading?: boolean;
  storageKey: string;
}

export const ValidationResultTable: FC<Props> = ({
  data,
  loading,
  storageKey,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useLocalStorage<number>(storageKey, 20);

  const classes = useStyles();

  const dataToRender = data?.map((i) => {
    let rowStatus = "NO CHANGE";
    if (i.isUpdate) {
      rowStatus = "UPDATE";
    }
    if (i.isAddition) {
      rowStatus = "ADDITION";
    }
    if (i.hasError) {
      rowStatus = "ERROR";
    }
    return {
      "Row Status": {
        isAddition: i.isAddition,
        isUpdate: i.isUpdate,
        hasError: i.hasError,
        value: rowStatus,
        errorMessage: i.errorMessage,
      },
      id: i.id,
      ...i.validationBreakdown,
    };
  });

  const columns: TableColumnDefinition[] = Object.keys(dataToRender[0])
    .filter((key) => key !== "id")
    .map((key) => ({
      field: key,
      headerName: key,
      flex: 1,
      renderCell: (value) => <ValidationTableCell {...value} />,
    }));

  return (
    <Table
      loading={loading}
      dense
      columns={columns}
      rows={
        dataToRender?.slice(
          currentPage * pageSize,
          currentPage * pageSize + pageSize,
        ) ?? []
      }
      classes={{
        rowClass: getRowClass(classes),
        rowCellClass: getRowCellClass(classes),
      }}
      hover={false}
      pagination={{
        page: currentPage,
        pageSize,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        totalRows: dataToRender?.length,
        handlePageChange: (newPageNumber) => {
          setCurrentPage(newPageNumber);
        },
        handlePageSizeChange: (newPageSize) => {
          setPageSize(newPageSize);
          setCurrentPage(0);
        },
      }}
    />
  );
};
